import { FC, ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAuth from 'src/hooks/useAuth';
import { useDispatch, useSelector } from 'src/store';
import { firebaseAppQP } from 'src/utils/firebaseQP';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { setSelectedProducer } from 'src/slices/auth/authSlice';
import { rolesApp } from 'src/utils/rolesConstants';

interface AuthenticatedProps {
  children: ReactNode;
}

const Authenticated: FC<AuthenticatedProps> = (props: { children: any }) => {
  const { children } = props;
  const auth = useAuth();
  const location = useLocation();
  const userQP = firebaseAppQP.auth().currentUser;
  const [requestedLocation, setRequestedLocation] = useState<string | null>(
    null
  );
  const { isMultiproducer, selectedProducer } = useSelector(
    (state) => state.auth
  );
  const dispatch = useDispatch();
  const { producers, profile } = useSelector((state) => state.auth);
  const isAuthBootstrapping = !auth.isInitialized;
  const isProducerInfoLoading =
    auth.isAuthenticated && selectedProducer === undefined;
  const requiresProducerSelection =
    auth.isAuthenticated && isMultiproducer && selectedProducer === null;

  useEffect(() => {
    console.log('Authenticated component mounted');
    return () => {
      console.log('Authenticated component unmounted');
    };
  }, []);

  if (isAuthBootstrapping || isProducerInfoLoading) {
    return <SuspenseLoader />;
  }

  if (!auth.isAuthenticated) {
    console.log('User not authenticated, redirecting to login');
    if (location.pathname !== requestedLocation) {
      setRequestedLocation(location.pathname);
    }
    return <Navigate to="/account/login" replace />;
  }

  if (requiresProducerSelection && location.pathname !== '/select-producer') {
    if (producers.length === 0) {
      return <Navigate to="/dashboard/management/unauthorized" replace />;
    }
    if (producers.length === 1) {
      dispatch(setSelectedProducer(producers[0]));
      sessionStorage.setItem('selectedProducer', JSON.stringify(producers[0]));
      return <Navigate to="/dashboard/management" replace />;
    }
    return <Navigate to="/select-producer" replace />;
  }

  if (requestedLocation && location.pathname !== requestedLocation) {
    setRequestedLocation(null);
    return (
      <Navigate to={requestedLocation || '/dashboard/management'} replace />
    );
  }

  if (!userQP) {
    console.log(' No userQP in firebase auth');
    return <Navigate to="/account/login" />;
  }

  return <>{children}</>;
};

Authenticated.propTypes = {
  children: PropTypes.node as PropTypes.Validator<ReactNode>
};

export default Authenticated;
